import React, { useEffect, useState } from "react";
import "./styles/dashboard.css";
import toast from "react-hot-toast";
import CommonHeader from "./components/CommonHeader";
import { hasPermission } from "./utils/permissionUtils";

function formatDisplayDate(dateString) {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return String(dateString);
  return d.toLocaleDateString("en-GB");
}

function formatFlightTimeAmPm(flightTime) {
  if (flightTime == null || flightTime === "") return "";
  const str = String(flightTime).trim();
  const match = str.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return "";
  let h = parseInt(match[1], 10);
  const m = match[2];
  if (Number.isNaN(h)) return "";
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${String(h12).padStart(2, "0")}:${m} ${period}`;
}

const BookingsByBookingDate = () => {
  const today = new Date().toISOString().slice(0, 10);

  const [agencies, setAgencies] = useState([]);
  const [times, setTimes] = useState([]);
  const [pickups, setPickups] = useState([]);
  const [agencyFk, setAgencyFk] = useState("");
  const [timeId, setTimeId] = useState("");
  const [pickupPointId, setPickupPointId] = useState("");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [bookings, setBookings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");

    fetch(`${process.env.REACT_APP_API_URL}/suggests/getagencies`, {
      method: "post",
      headers: { Authorization: token },
      body: [],
    }).then(async (res) => {
      const resp = await res.json();
      if (resp.list) setAgencies(resp.list);
    });

    fetch(`${process.env.REACT_APP_API_URL}/suggests/getbustimings`, {
      method: "post",
      headers: { Authorization: token },
      body: [],
    }).then(async (res) => {
      const resp = await res.json();
      if (resp.message === "OK") setTimes(resp.list || []);
    });

    fetch(`${process.env.REACT_APP_API_URL}/suggests/getpickuplocations`, {
      method: "post",
      headers: { Authorization: token },
      body: [],
    }).then(async (res) => {
      const resp = await res.json();
      if (resp.message === "OK") setPickups(resp.list || []);
    });
  }, []);

  const buildFilterBody = () => {
    const data = new URLSearchParams();
    data.append("from_date", fromDate);
    data.append("to_date", toDate);
    if (agencyFk) data.append("agency_fk", agencyFk);
    if (timeId) data.append("time_id", timeId);
    if (pickupPointId) data.append("pickup_point_id", pickupPointId);
    return data;
  };

  const handleSubmit = async () => {
    if (!fromDate || !toDate) {
      toast.error("From date and To date are required.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/booking/getlistByBookingDate`,
        {
          method: "POST",
          headers: { Authorization: localStorage.getItem("token") },
          body: buildFilterBody(),
        }
      );
      const result = await response.json();
      if (result.status === 1 && result.message === "OK") {
        setBookings(result.list || []);
        setMeta({
          from_date: result.from_date,
          to_date: result.to_date,
          total_bookings: result.total_bookings || 0,
        });
        if (!result.list || result.list.length === 0) {
          toast.error("No bookings found for the selected criteria");
        }
      } else {
        setBookings([]);
        setMeta(null);
        toast.error(result.message || "Failed to load bookings");
      }
    } catch (error) {
      console.error("Error loading bookings by booking date:", error);
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  const handleExcel = async () => {
    if (!bookings || bookings.length === 0) {
      toast.error("No data to export");
      return;
    }
    if (!fromDate || !toDate) {
      toast.error("From date and To date are required.");
      return;
    }

    setExcelLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/booking/generateExcelByBookingDate`,
        {
          method: "POST",
          headers: { Authorization: localStorage.getItem("token") },
          body: buildFilterBody(),
        }
      );
      const result = await response.json();
      if (result.status) {
        const link = document.createElement("a");
        link.href = result.download_url;
        link.download = result.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Excel file downloaded successfully");
      } else {
        toast.error(result.message || "Failed to generate Excel file");
      }
    } catch (error) {
      console.error("Error generating Excel:", error);
      toast.error("Failed to generate Excel file");
    } finally {
      setExcelLoading(false);
    }
  };

  if (!hasPermission("bookings-by-booking-date")) {
    return (
      <div className="dashboard">
        <CommonHeader />
        <div className="container mt-4">
          <div className="alert alert-danger">
            You don't have permission to access this page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <CommonHeader />
      <div className="d-flex flex-column flex-lg-row h-lg-full bg-surface-secondary">
        <div className="h-screen flex-grow-1 overflow-y-lg-auto">
          <header className="bg-surface-primary border-bottom pt-6">
            <div className="container-fluid">
              <div className="mb-npx">
                <div className="row align-items-center">
                  <div className="col-sm-6 col-12 mb-4 mb-sm-0">
                    <h1 className="h2 mb-0 ls-tight">Bookings by Booking Date</h1>
                    <p className="text-sm text-muted mb-0 mt-1">
                      Filter by when the booking was entered (not pickup date).
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <main className="py-6 bg-surface-secondary">
            <div className="container-fluid">
              <div className="card shadow border-0 mb-7">
                <div className="card-header">
                  <div className="row g-3 align-items-end">
                    <div className="col-md-2">
                      <label className="form-label">From (Booking Date)</label>
                      <input
                        type="date"
                        className="form-control"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">To (Booking Date)</label>
                      <input
                        type="date"
                        className="form-control"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Agency</label>
                      <select
                        className="form-select"
                        value={agencyFk}
                        onChange={(e) => setAgencyFk(e.target.value)}
                      >
                        <option value="">All</option>
                        {agencies.map((ag) => (
                          <option
                            key={ag.agency_id || ag.agency_fk}
                            value={ag.agency_id || ag.agency_fk}
                          >
                            {ag.agency_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Bus Time</label>
                      <select
                        className="form-select"
                        value={timeId}
                        onChange={(e) => setTimeId(e.target.value)}
                      >
                        <option value="">All</option>
                        {times.map((t) => (
                          <option key={t.time_id} value={t.time_id}>
                            {t.time_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Pickup Point</label>
                      <select
                        className="form-select"
                        value={pickupPointId}
                        onChange={(e) => setPickupPointId(e.target.value)}
                      >
                        <option value="">All</option>
                        {pickups.map((p) => (
                          <option
                            key={p.pickup_point_id}
                            value={p.pickup_point_id}
                          >
                            {p.pickup_point_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-2">
                      <button
                        className="btn btn-primary w-100"
                        onClick={handleSubmit}
                        disabled={loading}
                      >
                        {loading ? "Loading..." : "Submit"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover table-nowrap">
                    <thead className="thead-light">
                      <tr>
                        <th>Sl No</th>
                        <th>Booking Date</th>
                        <th>Pax Name</th>
                        <th>Contact No</th>
                        <th>Pickup Date</th>
                        <th>Bus Time</th>
                        <th>Pickup Point</th>
                        <th>Sub Pickup</th>
                        <th>Drop Point</th>
                        <th>Agency</th>
                        <th>Amount</th>
                        <th>Follow-up</th>
                        <th>Remarks</th>
                        <th>Added By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings && bookings.length > 0 ? (
                        bookings.map((booking, index) => {
                          const flight = formatFlightTimeAmPm(booking.flight_time);
                          const dropLabel = flight
                            ? `${booking.drop_point_name || ""} - ${flight}`
                            : booking.drop_point_name || "";
                          const rowStyle = booking.follow_up_status_color_code
                            ? { backgroundColor: booking.follow_up_status_color_code }
                            : {};
                          return (
                            <tr key={booking.booking_id} style={rowStyle}>
                              <td
                                style={{
                                  backgroundColor:
                                    booking.reach_inside_bus === "1"
                                      ? "#39FF14"
                                      : undefined,
                                }}
                              >
                                {index + 1}
                              </td>
                              <td>
                                {booking.booking_date_formatted ||
                                  formatDisplayDate(booking.booking_date)}
                              </td>
                              <td>{booking.pax_name}</td>
                              <td>
                                <a href={`tel:${booking.pax_contact_no}`}>
                                  {booking.pax_contact_no}
                                </a>
                              </td>
                              <td>{formatDisplayDate(booking.pickup_date)}</td>
                              <td>{booking.time_name}</td>
                              <td>{booking.pickup_point_name}</td>
                              <td>{booking.sub_pickup}</td>
                              <td>{dropLabel}</td>
                              <td>{booking.agency_name}</td>
                              <td>
                                {parseFloat(booking.amount || 0).toFixed(2)}
                              </td>
                              <td>{booking.follow_up_status_name || ""}</td>
                              <td>{booking.remarks}</td>
                              <td>{booking.added_user}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="14" className="text-center text-muted py-4">
                            {bookings === null
                              ? "Select booking date range and click Submit."
                              : "No bookings found."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="card-footer border-0 py-4 d-flex flex-wrap align-items-center gap-2">
                  {meta && (
                    <span className="text-sm text-muted me-auto">
                      Period: {formatDisplayDate(meta.from_date)} to{" "}
                      {formatDisplayDate(meta.to_date)} | Total:{" "}
                      {meta.total_bookings}
                    </span>
                  )}
                  <button
                    className="btn d-inline-flex btn-sm btn-primary mx-1"
                    onClick={handleExcel}
                    disabled={excelLoading || !bookings || bookings.length === 0}
                    title="Export full booking details to Excel"
                  >
                    <span className="pe-2">
                      <i className="bi bi-file-earmark-excel"></i>
                    </span>
                    <span>{excelLoading ? "Exporting..." : "Excel"}</span>
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default BookingsByBookingDate;
